/**
 * BUSINESS LAYER (BL): FIGHTERS
 * Capa de lógica de negocio, validaciones defensivas, utilidades matemáticas de juego,
 * manipulación de barajas con Fisher-Yates, sistema de combos y persistencia de sesión móvil.
 * 
 * @module utilities/BL/Fighters
 */

import {
    GetThemesDL,
    GetThemeByIdDL,
    CreateThemeDL,
    UpdateThemeDL,
    DeleteThemeDL,
    CloneThemeDL,
    GetConfigDL,
    SaveConfigDL,
    GetMatchHistoryDL,
    SaveMatchResultDL,
    UploadAssetDL,
    UploadBatchMediaDL,
    ShowCoverDL,
    ShowGameSceneDL,
    StartCountdownDL,
    PauseMatchDL,
    ResumeMatchDL,
    FinishEarlyDL,
    ResetMatchDL,
    GetMatchStateDL,
    SendTapBatchDL,
    RegisterFanDL,
    UnregisterFanDL
} from '../DL/Fighters';

import { SetScreenV2MainDynamic } from './ScreenV2';

// =========================================================================
// ENVOLTORIOS DE TEMAS VISUALES (CRUD)
// =========================================================================

/**
 * Obtiene el catálogo completo de temas disponibles para Fighters con validación de filtros.
 * @param {string|null} [roomId=null] - Identificador GUID de la sala (opcional).
 * @param {string|null} [operatorId=null] - Identificador GUID del operador (opcional).
 * @returns {Promise<{success: boolean, data: Array<Object>, message?: string}>} Colección de temas garantizada como arreglo.
 */
export const GetThemesBL = async (roomId = null, operatorId = null) => {
    try {
        const sanitizedRoomId = roomId ? String(roomId).trim() : null;
        const sanitizedOperatorId = operatorId ? String(operatorId).trim() : null;

        const dlResponse = await GetThemesDL(sanitizedRoomId, sanitizedOperatorId);
        if (!dlResponse.success) {
            return {
                success: false,
                data: [],
                message: dlResponse.message || 'No fue posible cargar el catálogo de temas de Fighters.'
            };
        }

        return {
            success: true,
            data: Array.isArray(dlResponse.data) ? dlResponse.data : []
        };
    } catch (unexpectedError) {
        return {
            success: false,
            data: [],
            message: 'Error inesperado al procesar la obtención de temas de Fighters.'
        };
    }
};

/**
 * Obtiene el detalle de un tema de Fighters validando la presencia del identificador.
 * @param {number|string} themeIdentifier - Identificador del tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Detalle del tema.
 */
export const GetThemeByIdBL = async (themeIdentifier) => {
    if (!themeIdentifier && themeIdentifier !== 0) {
        return {
            success: false,
            message: 'El identificador del tema es obligatorio.'
        };
    }

    return await GetThemeByIdDL(themeIdentifier);
};

/**
 * Sanea y crea un nuevo tema personalizado de Fighters con valores por defecto defensivos.
 * @param {Object} themeDataPayload - Datos del nuevo tema visual.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema creado y confirmado.
 */
export const CreateThemeBL = async (themeDataPayload, explicitRoomId = null, explicitOperatorId = null) => {
    if (!themeDataPayload || typeof themeDataPayload !== 'object') {
        return {
            success: false,
            message: 'El cuerpo de datos del tema es obligatorio.'
        };
    }

    const trimmedName = typeof themeDataPayload.name === 'string' ? themeDataPayload.name.trim() : '';
    if (!trimmedName) {
        return {
            success: false,
            message: 'El nombre del tema es obligatorio y no puede estar vacío.'
        };
    }

    const resolvedRoomId = themeDataPayload.roomId || explicitRoomId || null;
    const resolvedOperatorId = themeDataPayload.operatorId || explicitOperatorId || null;

    const sanitizedPayload = {
        name: trimmedName,
        roomId: resolvedRoomId,
        operatorId: resolvedOperatorId,
        isSystemPreset: false,
        brandLogoUrl: themeDataPayload.brandLogoUrl || null,
        sponsorLogoUrl: themeDataPayload.sponsorLogoUrl || null,
        coverTitle: themeDataPayload.coverTitle || '¡BATALLA DE LUCHADORES!',
        coverBackgroundType: themeDataPayload.coverBackgroundType || 'color',
        coverBackgroundColor: themeDataPayload.coverBackgroundColor || '#1a0b2e',
        coverBackgroundUrl: themeDataPayload.coverBackgroundUrl || null,
        coverTextColor: themeDataPayload.coverTextColor || '#ffffff',
        gameBackgroundType: themeDataPayload.gameBackgroundType || 'color',
        gameBackgroundColor: themeDataPayload.gameBackgroundColor || '#0f051d',
        gameBackgroundUrl: themeDataPayload.gameBackgroundUrl || null,
        gameTextColor: themeDataPayload.gameTextColor || '#ffffff',
        fanBackgroundType: themeDataPayload.fanBackgroundType || 'color',
        fanBackgroundColor: themeDataPayload.fanBackgroundColor || '#120826',
        fanBackgroundUrl: themeDataPayload.fanBackgroundUrl || null,
        fanTextColor: themeDataPayload.fanTextColor || '#ffffff',
        player1Avatar: themeDataPayload.player1Avatar || null,
        player1Winner: themeDataPayload.player1Winner || null,
        player1Color: themeDataPayload.player1Color || '#00ffcc',
        player2Avatar: themeDataPayload.player2Avatar || null,
        player2Winner: themeDataPayload.player2Winner || null,
        player2Color: themeDataPayload.player2Color || '#ff0055',
        fontFamily: themeDataPayload.fontFamily || 'Bangers',
        deckImages: typeof themeDataPayload.deckImages === 'string'
            ? themeDataPayload.deckImages
            : JSON.stringify(themeDataPayload.deckImages || []),
        comboTexts: typeof themeDataPayload.comboTexts === 'string'
            ? themeDataPayload.comboTexts
            : JSON.stringify(themeDataPayload.comboTexts || [])
    };

    return await CreateThemeDL(sanitizedPayload);
};

/**
 * Actualiza un tema existente validando identificador y nombres obligatorios.
 * @param {number|string} themeIdentifier - Identificador del tema a actualizar.
 * @param {Object} themeDataPayload - Datos actualizados del tema.
 * @param {string|null} [explicitRoomId=null] - Identificador GUID opcional de la sala.
 * @param {string|null} [explicitOperatorId=null] - Identificador GUID opcional del operador.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado de la actualización.
 */
export const UpdateThemeBL = async (themeIdentifier, themeDataPayload, explicitRoomId = null, explicitOperatorId = null) => {
    if (!themeIdentifier && themeIdentifier !== 0) {
        return {
            success: false,
            message: 'El identificador del tema es obligatorio.'
        };
    }

    if (!themeDataPayload || typeof themeDataPayload !== 'object') {
        return {
            success: false,
            message: 'El cuerpo de datos para actualizar el tema es obligatorio.'
        };
    }

    const trimmedName = typeof themeDataPayload.name === 'string' ? themeDataPayload.name.trim() : '';
    if (!trimmedName) {
        return {
            success: false,
            message: 'El nombre del tema es obligatorio y no puede estar vacío.'
        };
    }

    const resolvedRoomId = themeDataPayload.roomId || explicitRoomId || null;
    const resolvedOperatorId = themeDataPayload.operatorId || explicitOperatorId || null;

    const sanitizedPayload = {
        name: trimmedName,
        roomId: resolvedRoomId,
        operatorId: resolvedOperatorId,
        brandLogoUrl: themeDataPayload.brandLogoUrl || null,
        sponsorLogoUrl: themeDataPayload.sponsorLogoUrl || null,
        coverTitle: themeDataPayload.coverTitle || '¡BATALLA DE LUCHADORES!',
        coverBackgroundType: themeDataPayload.coverBackgroundType || 'color',
        coverBackgroundColor: themeDataPayload.coverBackgroundColor || '#1a0b2e',
        coverBackgroundUrl: themeDataPayload.coverBackgroundUrl || null,
        coverTextColor: themeDataPayload.coverTextColor || '#ffffff',
        gameBackgroundType: themeDataPayload.gameBackgroundType || 'color',
        gameBackgroundColor: themeDataPayload.gameBackgroundColor || '#0f051d',
        gameBackgroundUrl: themeDataPayload.gameBackgroundUrl || null,
        gameTextColor: themeDataPayload.gameTextColor || '#ffffff',
        fanBackgroundType: themeDataPayload.fanBackgroundType || 'color',
        fanBackgroundColor: themeDataPayload.fanBackgroundColor || '#120826',
        fanBackgroundUrl: themeDataPayload.fanBackgroundUrl || null,
        fanTextColor: themeDataPayload.fanTextColor || '#ffffff',
        player1Avatar: themeDataPayload.player1Avatar || null,
        player1Winner: themeDataPayload.player1Winner || null,
        player1Color: themeDataPayload.player1Color || '#00ffcc',
        player2Avatar: themeDataPayload.player2Avatar || null,
        player2Winner: themeDataPayload.player2Winner || null,
        player2Color: themeDataPayload.player2Color || '#ff0055',
        fontFamily: themeDataPayload.fontFamily || 'Bangers',
        deckImages: typeof themeDataPayload.deckImages === 'string'
            ? themeDataPayload.deckImages
            : JSON.stringify(themeDataPayload.deckImages || []),
        comboTexts: typeof themeDataPayload.comboTexts === 'string'
            ? themeDataPayload.comboTexts
            : JSON.stringify(themeDataPayload.comboTexts || [])
    };

    return await UpdateThemeDL(themeIdentifier, sanitizedPayload);
};

/**
 * Elimina un tema personalizado de Fighters.
 * @param {number|string} themeIdentifier - Identificador del tema a eliminar.
 * @returns {Promise<{success: boolean, message?: string}>} Confirmación de eliminación.
 */
export const DeleteThemeBL = async (themeIdentifier) => {
    if (!themeIdentifier && themeIdentifier !== 0) {
        return {
            success: false,
            message: 'El identificador del tema a eliminar es obligatorio.'
        };
    }

    return await DeleteThemeDL(themeIdentifier);
};

/**
 * Clona un tema existente generando una nueva copia editable e independiente.
 * @param {number|string} themeIdentifier - Identificador del tema original.
 * @param {string|null} [newThemeName=null] - Nombre para la nueva copia (opcional).
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema clonado.
 */
export const CloneThemeBL = async (themeIdentifier, newThemeName = null) => {
    if (!themeIdentifier && themeIdentifier !== 0) {
        return {
            success: false,
            message: 'El identificador del tema a clonar es obligatorio.'
        };
    }

    const sanitizedName = typeof newThemeName === 'string' && newThemeName.trim() ? newThemeName.trim() : null;
    return await CloneThemeDL(themeIdentifier, sanitizedName);
};

// =========================================================================
// CONFIGURACIÓN E HISTORIAL DE SALA
// =========================================================================

/**
 * Obtiene la configuración activa de Fighters para una sala validando el identificador.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración activa.
 */
export const GetConfigBL = async (roomId) => {
    const effectiveRoomId = roomId || (typeof localStorage !== 'undefined'
        ? (localStorage.getItem('active_room_id') || localStorage.getItem('roomId') || localStorage.getItem('selected_room_id') || localStorage.getItem('RoomId'))
        : '') || '';

    if (!effectiveRoomId) {
        return {
            success: false,
            message: 'El identificador de la sala (RoomId) es obligatorio.'
        };
    }

    return await GetConfigDL(String(effectiveRoomId).trim());
};

/**
 * Guarda o actualiza la configuración de Fighters para una sala con valores normalizados.
 * @param {Object|string} param1 - Datos de configuración de la sala o identificador de sala.
 * @param {string|Object} [param2] - Identificador de sala o datos de configuración.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración guardada.
 */
export const SaveConfigBL = async (param1, param2) => {
    let payload = {};
    let roomId = '';

    if (param1 && typeof param1 === 'object') {
        payload = { ...param1 };
        roomId = param2 || payload.roomId || '';
    } else if (typeof param1 === 'string' && param2 && typeof param2 === 'object') {
        payload = { ...param2 };
        roomId = param1;
    } else if (typeof param1 === 'string') {
        roomId = param1;
    }

    if (!roomId) {
        roomId = (typeof localStorage !== 'undefined'
            ? (localStorage.getItem('active_room_id') || localStorage.getItem('roomId') || localStorage.getItem('selected_room_id') || localStorage.getItem('RoomId'))
            : '') || '';
    }

    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de sala (RoomId) es obligatorio para guardar la configuración.'
        };
    }

    const sanitizedConfig = {
        roomId: String(roomId).trim(),
        activeThemeId: payload.activeThemeId !== undefined && payload.activeThemeId !== null
            ? (Number(payload.activeThemeId) > 0 ? Number(payload.activeThemeId) : null)
            : null,
        gameMode: payload.gameMode || 'Score',
        roundDurationSeconds: Math.max(5, Math.min(60, Number(payload.roundDurationSeconds || payload.roundDuration) || 10)),
        showComboPopups: payload.showComboPopups !== undefined ? Boolean(payload.showComboPopups) : true,
        showDeckTransforms: payload.showDeckTransforms !== undefined ? Boolean(payload.showDeckTransforms) : true,
        deckTransformInterval: Math.max(1, Math.min(100, Number(payload.deckTransformInterval) || 15))
    };

    return await SaveConfigDL(sanitizedConfig);
};

/**
 * Obtiene el historial de partidas disputadas en una sala garantizando una lista estructurada.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {number} [limitQuantity=20] - Límite de partidas a recuperar.
 * @returns {Promise<{success: boolean, data: Array<Object>, message?: string}>} Lista de partidas.
 */
export const GetMatchHistoryBL = async (param1, param2) => {
    let effectiveRoomId = '';
    let effectiveLimit = 20;

    // Detect if invoked as (limit, roomId) or (roomId, limit)
    if (typeof param1 === 'number' || (!isNaN(Number(param1)) && typeof param2 === 'string' && param2.length > 10)) {
        effectiveLimit = Number(param1) || 20;
        effectiveRoomId = param2 || '';
    } else {
        effectiveRoomId = param1 || '';
        effectiveLimit = Number(param2) || 20;
    }

    if (!effectiveRoomId || !String(effectiveRoomId).trim() || !isNaN(Number(effectiveRoomId))) {
        return {
            success: false,
            data: [],
            message: 'El identificador de la sala es obligatorio para consultar el historial.'
        };
    }

    const sanitizedLimit = Math.max(1, Math.min(100, Number(effectiveLimit) || 20));
    const dlResponse = await GetMatchHistoryDL(String(effectiveRoomId).trim(), sanitizedLimit);

    if (!dlResponse.success) {
        return {
            success: false,
            data: [],
            message: dlResponse.message || 'No fue posible consultar el historial de partidas.'
        };
    }

    return {
        success: true,
        data: Array.isArray(dlResponse.data) ? dlResponse.data : []
    };
};

/**
 * Registra y persiste las estadísticas de una partida disputada.
 * @param {Object} matchPayload - Métricas finales de la partida.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado persistido.
 */
export const SaveMatchResultBL = async (matchPayload) => {
    if (!matchPayload || !matchPayload.roomId) {
        return {
            success: false,
            message: 'El identificador de la sala es obligatorio para guardar el resultado de la partida.'
        };
    }

    const sanitizedMatch = {
        matchId: matchPayload.matchId || undefined,
        roomId: String(matchPayload.roomId).trim(),
        operatorId: matchPayload.operatorId || null,
        playedAt: matchPayload.playedAt || new Date().toISOString(),
        themeId: matchPayload.themeId ? Number(matchPayload.themeId) : null,
        themeName: matchPayload.themeName || null,
        gameMode: matchPayload.gameMode || 'Simultaneous',
        winner: matchPayload.winner || 'Empate',
        scoreJ1: Number(matchPayload.scoreJ1) || 0,
        scoreJ2: Number(matchPayload.scoreJ2) || 0,
        totalTapsJ1: Math.max(0, Number(matchPayload.totalTapsJ1) || 0),
        totalTapsJ2: Math.max(0, Number(matchPayload.totalTapsJ2) || 0),
        activeFansJ1: Math.max(0, Number(matchPayload.activeFansJ1) || 0),
        activeFansJ2: Math.max(0, Number(matchPayload.activeFansJ2) || 0),
        effectiveDurationSeconds: Math.max(1, Number(matchPayload.effectiveDurationSeconds) || 10)
    };

    return await SaveMatchResultDL(sanitizedMatch);
};

// =========================================================================
// CARGA MULTIMEDIA
// =========================================================================

/**
 * Valida y sube un archivo gráfico para Fighters.
 * @param {File|Blob|FormData} fileOrFormData - Archivo a transmitir.
 * @returns {Promise<{success: boolean, data?: Object, url?: string, message?: string}>} URL del archivo.
 */
export const UploadAssetBL = async (fileOrFormData) => {
    if (!fileOrFormData) {
        return {
            success: false,
            message: 'No se ha seleccionado ningún archivo para cargar.'
        };
    }

    return await UploadAssetDL(fileOrFormData);
};

/**
 * Valida y sube un lote masivo de hasta 50 archivos multimedia para la baraja o recursos de Fighters.
 * @param {Array<File>|FileList|FormData} filesOrFormData - Colección de archivos a transmitir.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Reporte de procesamiento.
 */
export const UploadBatchMediaBL = async (filesOrFormData) => {
    if (!filesOrFormData) {
        return {
            success: false,
            message: 'No se seleccionaron archivos para la carga masiva.'
        };
    }

    if (!(filesOrFormData instanceof FormData)) {
        const filesArray = Array.from(filesOrFormData || []);
        if (filesArray.length === 0) {
            return {
                success: false,
                message: 'La lista de archivos a cargar está vacía.'
            };
        }

        if (filesArray.length > 50) {
            return {
                success: false,
                message: 'El límite máximo para subida por lote es de 50 archivos.'
            };
        }
    }

    return await UploadBatchMediaDL(filesOrFormData);
};

// =========================================================================
// COMANDOS DE CONTROL DE SALA EN TIEMPO REAL
// =========================================================================

/**
 * Proyecta la portada de Fighters en la pantalla gigante y notifica al lienzo ScreenV2.
 * @param {string|null} [roomId=null] - Identificador GUID de la sala.
 * @param {number|string|null} [themeIdentifier=null] - Identificador del tema visual.
 * @param {number} [screenIdentifier=1] - Identificador de la pantalla en ScreenV2.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado de la portada.
 */
export const ShowCoverBL = async (roomId = null, themeIdentifier = null, screenIdentifier = 1) => {
    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para proyectar la portada.'
        };
    }

    try {
        const dlResponse = await ShowCoverDL(roomId, themeIdentifier || 0);

        try {
            await SetScreenV2MainDynamic(screenIdentifier, 'FightersCover');
        } catch (screenError) {
            // Silencioso si ScreenV2 no está activo en este entorno
        }

        return dlResponse.success
            ? { success: true, data: dlResponse.data, message: 'Portada de Fighters proyectada exitosamente.' }
            : dlResponse;
    } catch (unexpectedError) {
        return {
            success: false,
            message: 'Error al proyectar la portada de Fighters.'
        };
    }
};

/**
 * Proyecta la escena de combate (ring) con los avatares y barras de vida preparadas.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {number|string} [themeIdentifier=null] - Identificador del tema visual.
 * @param {string} [gameMode='Score'] - Modo de combate ('Score' | 'HealthKO' | 'HealthTimed' | 'Simultaneous').
 * @param {number} [durationSeconds=10] - Duración del asalto en segundos.
 * @param {number} [screenIdentifier=1] - Identificador de la pantalla en ScreenV2.
 * @param {boolean} [showComboPopups=true] - Habilitar popups de combo.
 * @param {boolean} [showDeckTransforms=true] - Habilitar transformaciones de baraja.
 * @param {number} [deckTransformInterval=15] - Frecuencia de cambio de imagen en taps.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado de la escena de juego.
 */
export const ShowGameSceneBL = async (
    roomId = null,
    themeIdentifier = null,
    gameMode = 'Score',
    durationSeconds = 10,
    screenIdentifier = 1,
    showComboPopups = true,
    showDeckTransforms = true,
    deckTransformInterval = 15
) => {
    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para proyectar el combate.'
        };
    }

    try {
        const sanitizedDuration = Math.max(5, Math.min(60, Number(durationSeconds) || 10));
        const dlResponse = await ShowGameSceneDL(
            roomId,
            themeIdentifier || 0,
            gameMode,
            sanitizedDuration,
            showComboPopups,
            showDeckTransforms,
            deckTransformInterval
        );

        try {
            await SetScreenV2MainDynamic(screenIdentifier, 'Fighters');
        } catch (screenError) {
            // Silencioso si ScreenV2 no está activo en este entorno
        }

        return dlResponse.success
            ? { success: true, data: dlResponse.data, message: 'Escena de combate proyectada exitosamente.' }
            : dlResponse;
    } catch (unexpectedError) {
        return {
            success: false,
            message: 'Error al proyectar la escena de combate de Fighters.'
        };
    }
};

/**
 * Inicia la cuenta regresiva sincronizada (3, 2, 1) y arranque del combate.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado del combate iniciado.
 */
export const StartCountdownBL = async (roomId) => {
    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de la sala es obligatorio para iniciar el combate.'
        };
    }

    return await StartCountdownDL(roomId);
};

/**
 * Pausa temporalmente el combate activo en la sala.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de pausa.
 */
export const PauseMatchBL = async (roomId) => {
    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para pausar el combate.'
        };
    }

    return await PauseMatchDL(roomId);
};

/**
 * Reanuda el combate activo en la sala.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de reanudación.
 */
export const ResumeMatchBL = async (roomId) => {
    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para reanudar el combate.'
        };
    }

    return await ResumeMatchDL(roomId);
};

/**
 * Finaliza anticipadamente el combate activo, calcula el ganador y persiste en base de datos.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado final calculado.
 */
export const FinishEarlyBL = async (roomId) => {
    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para finalizar el combate.'
        };
    }

    return await FinishEarlyDL(roomId);
};

/**
 * Reinicia la partida de la sala devolviéndola al estado inactivo.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado reiniciado.
 */
export const ResetMatchBL = async (roomId) => {
    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para reiniciar el combate.'
        };
    }

    return await ResetMatchDL(roomId);
};

/**
 * Obtiene el estado actual en memoria de la partida de Fighters.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado en vivo.
 */
export const GetMatchStateBL = async (roomId) => {
    if (!roomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para consultar el estado de la partida.'
        };
    }

    return await GetMatchStateDL(roomId);
};

// =========================================================================
// TELEMETRÍA Y AFICIONADOS
// =========================================================================

/**
 * Valida y transmite un lote de toques (taps) acumulados por el aficionado móvil.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string} playerChoice - Bando seleccionado ('player1' | 'player2').
 * @param {number} tapCount - Cantidad de toques acumulados.
/**
 * Procesa y envía un lote de toques (taps) a la sala aplicando sanitización y resolución de bando.
 * @param {string|Object} param1 - Identificador de sala o carga útil.
 * @param {string|number} [param2] - Bando ('player1'|'player2'|'J1'|'J2'), connectionId o tapCount.
 * @param {number|string} [param3] - tapCount o bando.
 * @param {string} [param4] - Identificador SignalR de la conexión activa.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado del procesamiento.
 */
export const SendTapBatchBL = async (param1, param2, param3, param4) => {
    let effectiveRoomId = '';
    let effectiveChoice = 'player1';
    let effectiveTaps = 1;
    let effectiveConnectionId = '';

    const args = [param1, param2, param3, param4].filter(x => x !== undefined && x !== null);

    // Si el primer parámetro es un objeto estructurado
    if (param1 && typeof param1 === 'object') {
        effectiveRoomId = param1.roomId || param2 || '';
        effectiveChoice = param1.playerChoice || param1.side || 'player1';
        effectiveTaps = Number(param1.tapCount || param1.taps) || 1;
        effectiveConnectionId = param1.connectionId || '';
    } else {
        effectiveRoomId = String(param1 || '').trim();

        for (let i = 1; i < args.length; i++) {
            const val = args[i];
            if (typeof val === 'number' || (!isNaN(Number(val)) && typeof val !== 'string')) {
                effectiveTaps = Number(val);
            } else if (typeof val === 'string') {
                const lower = val.toLowerCase().trim();
                if (lower === 'player1' || lower === 'j1' || lower === '1') {
                    effectiveChoice = 'player1';
                } else if (lower === 'player2' || lower === 'j2' || lower === '2') {
                    effectiveChoice = 'player2';
                } else if (lower.length > 5 && !effectiveConnectionId) {
                    effectiveConnectionId = val;
                }
            }
        }
    }

    // Normalizar choice
    const normLower = String(effectiveChoice).toLowerCase().trim();
    if (normLower === 'j2' || normLower === 'player2' || normLower === '2') {
        effectiveChoice = 'player2';
    } else {
        effectiveChoice = 'player1';
    }

    if (!effectiveRoomId) {
        effectiveRoomId = (typeof localStorage !== 'undefined'
            ? (localStorage.getItem('active_room_id') || localStorage.getItem('roomId') || localStorage.getItem('selected_room_id') || localStorage.getItem('RoomId'))
            : '') || '';
    }

    if (!effectiveRoomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para registrar toques.'
        };
    }

    const sanitizedTaps = Math.max(1, Math.min(100, Number(effectiveTaps) || 1));
    return await SendTapBatchDL(effectiveRoomId, effectiveChoice, sanitizedTaps, effectiveConnectionId);
};

/**
 * Registra formalmente al aficionado en un bando para el cálculo del promedio ponderado.
 * @param {string} param1 - Identificador GUID de la sala o payload.
 * @param {string} [param2] - Bando seleccionado ('player1' | 'player2' | 'J1' | 'J2').
 * @param {string} [param3] - Identificador SignalR de la conexión activa.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado actualizado.
 */
export const RegisterFanBL = async (param1, param2, param3) => {
    let effectiveRoomId = '';
    let effectiveChoice = 'player1';
    let effectiveConnectionId = '';

    const args = [param1, param2, param3].filter(x => x !== undefined && x !== null);

    if (param1 && typeof param1 === 'object') {
        effectiveRoomId = param1.roomId || param2 || '';
        effectiveChoice = param1.playerChoice || param1.side || 'player1';
        effectiveConnectionId = param1.connectionId || '';
    } else {
        effectiveRoomId = String(param1 || '').trim();

        for (let i = 1; i < args.length; i++) {
            const val = args[i];
            if (typeof val === 'string') {
                const lower = val.toLowerCase().trim();
                if (lower === 'player1' || lower === 'j1' || lower === '1') {
                    effectiveChoice = 'player1';
                } else if (lower === 'player2' || lower === 'j2' || lower === '2') {
                    effectiveChoice = 'player2';
                } else if (lower.length > 5 && !effectiveConnectionId) {
                    effectiveConnectionId = val;
                }
            }
        }
    }

    const normLower = String(effectiveChoice).toLowerCase().trim();
    if (normLower === 'j2' || normLower === 'player2' || normLower === '2') {
        effectiveChoice = 'player2';
    } else {
        effectiveChoice = 'player1';
    }

    if (!effectiveRoomId) {
        effectiveRoomId = (typeof localStorage !== 'undefined'
            ? (localStorage.getItem('active_room_id') || localStorage.getItem('roomId') || localStorage.getItem('selected_room_id') || localStorage.getItem('RoomId'))
            : '') || '';
    }

    if (!effectiveRoomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para registrar al aficionado.'
        };
    }

    return await RegisterFanDL(effectiveRoomId, effectiveChoice, effectiveConnectionId);
};

/**
 * Desregistra formalmente al aficionado del bando activo al salir o desconectarse.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string} [connectionId=''] - Identificador SignalR de la conexión.
 * @returns {Promise<{success: boolean, message?: string}>} Confirmación de desregistro.
 */
export const UnregisterFanBL = async (roomId, connectionId = '') => {
    const effectiveRoomId = roomId || (typeof localStorage !== 'undefined'
        ? (localStorage.getItem('active_room_id') || localStorage.getItem('roomId') || localStorage.getItem('selected_room_id') || localStorage.getItem('RoomId'))
        : '') || '';

    if (!effectiveRoomId) {
        return {
            success: false,
            message: 'El identificador de sala es obligatorio para desregistrar al aficionado.'
        };
    }

    return await UnregisterFanDL(effectiveRoomId, connectionId);
};

// =========================================================================
// UTILIDADES MATEMÁTICAS Y DE JUEGO
// =========================================================================

/**
 * Calcula el promedio ponderado de toques por aficionado activo aplicando reglas de neutralidad.
 * Fórmula autoritativa: Math.max(0, totalTaps) / Math.max(1, activeFans)
 * @param {number} totalTaps - Total de toques acumulados por el bando.
 * @param {number} activeFans - Cantidad de aficionados conectados activamente al bando.
 * @returns {number} Promedio ponderado redondeado a 2 decimales.
 */
export const CalculateWeightedAverage = (totalTaps, activeFans) => {
    const validTaps = Math.max(0, Number(totalTaps) || 0);
    const validFans = Math.max(1, Number(activeFans) || 0);
    const averageScore = validTaps / validFans;
    return Math.round(averageScore * 100) / 100;
};

/**
 * Evalúa el nivel de combo alcanzado a partir del puntaje promedio ponderado del luchador.
 * Soporta los 5 niveles base (10, 20, 30, 40, 50) y los niveles crecientes de Modo Dios (+50 extras).
 * @param {number} scoreAverage - Puntaje promedio ponderado actual del luchador.
 * @param {Array<Object>|string} [customComboTexts=[]] - Configuración de textos personalizados (opcional).
 * @returns {{
 *   level: number,
 *   threshold: number,
 *   text: string,
 *   color: string,
 *   fontSize: string,
 *   extraPoints: number,
 *   isGodMode: boolean
 * }} Información del combo alcanzado para efectos visuales.
 */
export const GetComboForScore = (scoreAverage, customComboTexts = []) => {
    const validScore = Math.max(0, Number(scoreAverage) || 0);
    const integerScore = Math.floor(validScore);

    // Parsear textos personalizados si vienen serializados en JSON
    let parsedCustomList = [];
    if (typeof customComboTexts === 'string') {
        try {
            parsedCustomList = JSON.parse(customComboTexts);
        } catch (jsonError) {
            parsedCustomList = [];
        }
    } else if (Array.isArray(customComboTexts)) {
        parsedCustomList = customComboTexts;
    }

    // Configuración base de los 5 niveles predeterminados
    const defaultComboLevels = [
        {
            level: 1,
            threshold: 10,
            text: '¡GOLPE RÁPIDO!',
            color: '#3b82f6',
            fontSize: '1.2rem',
            extraPoints: 10,
            isGodMode: false
        },
        {
            level: 2,
            threshold: 20,
            text: '¡SUPER COMBO!',
            color: '#10b981',
            fontSize: '1.4rem',
            extraPoints: 25,
            isGodMode: false
        },
        {
            level: 3,
            threshold: 30,
            text: '¡ULTRA COMBO!',
            color: '#f59e0b',
            fontSize: '1.6rem',
            extraPoints: 50,
            isGodMode: false
        },
        {
            level: 4,
            threshold: 40,
            text: '¡GOLPE CRÍTICO!',
            color: '#ec4899',
            fontSize: '1.8rem',
            extraPoints: 80,
            isGodMode: false
        },
        {
            level: 5,
            threshold: 50,
            text: '¡MODO DIOS!',
            color: '#8b5cf6',
            fontSize: '2.2rem',
            extraPoints: 120,
            isGodMode: true
        }
    ];

    // Reemplazar textos si existen en la configuración personalizada
    const combinedLevels = defaultComboLevels.map((comboItem, itemIndex) => {
        const customEntry = parsedCustomList[itemIndex];
        if (customEntry) {
            if (typeof customEntry === 'string') {
                return { ...comboItem, text: customEntry };
            }
            if (typeof customEntry === 'object' && customEntry.text) {
                return {
                    ...comboItem,
                    text: customEntry.text,
                    color: customEntry.color || comboItem.color
                };
            }
        }
        return comboItem;
    });

    // Si el score supera 50, generar escalonamiento de Modo Dios cada +50 (100, 150, 200, ...) hasta un máximo de 20 niveles
    if (integerScore >= 100) {
        const godStepCount = Math.min(20, Math.floor((integerScore - 50) / 50));
        for (let stepIndex = 1; stepIndex <= godStepCount; stepIndex++) {
            const godThreshold = 50 + (stepIndex * 50);
            const godLevelNumber = 5 + stepIndex;
            combinedLevels.push({
                level: godLevelNumber,
                threshold: godThreshold,
                text: `¡FURIA IMPARABLE x${godLevelNumber}!`,
                color: '#ef4444',
                fontSize: '2.5rem',
                extraPoints: 120 + (stepIndex * 50),
                isGodMode: true
            });
        }
    }

    // Filtrar niveles alcanzados y tomar el de mayor umbral
    const achievedLevels = combinedLevels.filter((comboItem) => integerScore >= comboItem.threshold);
    if (achievedLevels.length === 0) {
        return {
            level: 0,
            threshold: 0,
            text: '',
            color: '#ffffff',
            fontSize: '1rem',
            extraPoints: 0,
            isGodMode: false
        };
    }

    return achievedLevels[achievedLevels.length - 1];
};

/**
 * Implementa el algoritmo de barajado Fisher-Yates (Knuth) sin sesgo estadístico para barajas de 1 a 50 slots.
 * Retorna una copia pura sin mutar el arreglo original.
 * @template T
 * @param {Array<T>} cardsArray - Arreglo de cartas o elementos multimedia a barajar.
 * @returns {Array<T>} Nuevo arreglo barajado de forma determinista y aleatoria.
 */
export const ShuffleDeckFisherYates = (cardsArray) => {
    if (!Array.isArray(cardsArray) || cardsArray.length <= 1) {
        return Array.isArray(cardsArray) ? [...cardsArray] : [];
    }

    const shuffledClone = [...cardsArray];
    for (let currentIndex = shuffledClone.length - 1; currentIndex > 0; currentIndex--) {
        const randomTargetIndex = Math.floor(Math.random() * (currentIndex + 1));
        const temporaryElement = shuffledClone[currentIndex];
        shuffledClone[currentIndex] = shuffledClone[randomTargetIndex];
        shuffledClone[randomTargetIndex] = temporaryElement;
    }

    return shuffledClone;
};

// =========================================================================
// UTILIDADES DE SESIÓN MÓVIL DEL AFICIONADO
// =========================================================================

/**
 * Genera la clave de almacenamiento segura y aislada por sala para la sesión de aficionado móvil.
 * @param {string} [roomId=''] - Identificador GUID de la sala.
 * @returns {string} Clave de almacenamiento.
 */
const getFanSessionStorageKey = (roomId = '') => {
    const sanitizedRoomId = roomId ? String(roomId).trim() : 'default';
    return `fighters_fan_session_${sanitizedRoomId}`;
};

/**
 * Recupera de forma segura los datos de sesión móvil del aficionado activo para una sala.
 * @param {string} [roomId=''] - Identificador GUID de la sala.
 * @returns {{roomId: string, playerChoice: string, registeredAt: string}|null} Datos de sesión o null si no existen o son inválidos.
 */
export const GetFanSessionData = (roomId = '') => {
    try {
        const storageKey = getFanSessionStorageKey(roomId);
        const rawSessionString = localStorage.getItem(storageKey)
            || sessionStorage.getItem(storageKey);

        if (!rawSessionString) {
            return null;
        }

        const parsedSession = JSON.parse(rawSessionString);
        if (
            parsedSession &&
            parsedSession.roomId &&
            (parsedSession.playerChoice === 'player1' || parsedSession.playerChoice === 'player2')
        ) {
            return {
                roomId: String(parsedSession.roomId),
                playerChoice: String(parsedSession.playerChoice),
                registeredAt: parsedSession.registeredAt || new Date().toISOString()
            };
        }

        return null;
    } catch (storageError) {
        return null;
    }
};

/**
 * Guarda los datos de sesión activa del aficionado móvil en el almacenamiento del navegador aislando por sala.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string} playerChoice - Bando seleccionado ('player1' | 'player2').
 * @returns {boolean} Verdadero si la sesión se persistió con éxito.
 */
export const SaveFanSessionData = (roomId, playerChoice) => {
    if (!roomId) {
        return false;
    }

    if (playerChoice !== 'player1' && playerChoice !== 'player2') {
        return false;
    }

    try {
        const sanitizedRoomId = String(roomId).trim();
        const storageKey = getFanSessionStorageKey(sanitizedRoomId);
        const sessionPayload = {
            roomId: sanitizedRoomId,
            playerChoice: String(playerChoice).trim(),
            registeredAt: new Date().toISOString()
        };

        const serializedPayload = JSON.stringify(sessionPayload);
        localStorage.setItem(storageKey, serializedPayload);
        sessionStorage.setItem(storageKey, serializedPayload);
        return true;
    } catch (storageError) {
        return false;
    }
};

/**
 * Elimina la sesión móvil activa del aficionado para la sala especificada.
 * @param {string} [roomId=''] - Identificador GUID de la sala a limpiar.
 * @returns {boolean} Verdadero si se limpió la sesión satisfactoriamente.
 */
export const ClearFanSessionData = (roomId = '') => {
    try {
        const storageKey = getFanSessionStorageKey(roomId);
        localStorage.removeItem(storageKey);
        sessionStorage.removeItem(storageKey);
        return true;
    } catch (storageError) {
        return false;
    }
};

// =========================================================================
// ALIASES DE COMPATIBILIDAD CON ADSL (Farmeando Aura)
// =========================================================================
export const GetThemes = (roomId, opId) => GetThemesBL(roomId, opId);
export const GetTheme = (id, roomId) => GetThemeByIdBL(id, roomId);
export const CreateTheme = (data, roomId, opId) => CreateThemeBL(data, roomId, opId);
export const UpdateTheme = (id, data, roomId, opId) => UpdateThemeBL(id, data, roomId, opId);
export const SaveTheme = (data, roomId, opId) => {
    if (data && (Number(data.id) > 0 || Number(data.themeId) > 0)) {
        const id = data.id || data.themeId;
        return UpdateThemeBL(id, data, roomId, opId);
    }
    return CreateThemeBL(data, roomId, opId);
};
export const DeleteTheme = (id, roomId, opId) => DeleteThemeBL(id, roomId, opId);
export const CloneTheme = (id, roomId, opId) => CloneThemeBL(id, roomId, opId);
export const GetConfig = (roomId) => GetConfigBL(roomId);
export const SaveConfig = (data, roomId) => SaveConfigBL(data, roomId);
export const GetMatchesHistory = (param1, param2) => GetMatchHistoryBL(param1, param2);
export const UploadMedia = (file, category, roomId) => UploadAssetBL(file, category, roomId);
export const UploadBatchMedia = (files, category, roomId) => UploadBatchMediaBL(files, category, roomId);
export const ShowCover = (roomId, themeId) => ShowCoverBL(roomId, themeId);
export const ShowGame = (roomId, themeId, mode, dur) => ShowGameSceneBL(roomId, themeId, mode, dur);
export const StartGame = (dur, mode, roomId) => StartCountdownBL(roomId, dur, mode);
export const PauseGame = (roomId) => PauseMatchBL(roomId);
export const ResumeGame = (roomId) => ResumeMatchBL(roomId);
export const FinishEarly = (roomId) => FinishEarlyBL(roomId);
export const ResetGame = (roomId) => ResetMatchBL(roomId);
export const GetRealtimeState = (roomId) => GetMatchStateBL(roomId);

export default {
    GetThemesBL,
    GetThemeByIdBL,
    CreateThemeBL,
    UpdateThemeBL,
    DeleteThemeBL,
    CloneThemeBL,
    GetConfigBL,
    SaveConfigBL,
    GetMatchHistoryBL,
    SaveMatchResultBL,
    UploadAssetBL,
    UploadBatchMediaBL,
    ShowCoverBL,
    ShowGameSceneBL,
    StartCountdownBL,
    PauseMatchBL,
    ResumeMatchBL,
    FinishEarlyBL,
    ResetMatchBL,
    GetMatchStateBL,
    SendTapBatchBL,
    RegisterFanBL,
    UnregisterFanBL,
    CalculateWeightedAverage,
    GetComboForScore,
    ShuffleDeckFisherYates,
    GetFanSessionData,
    SaveFanSessionData,
    ClearFanSessionData,
    GetThemes,
    GetTheme,
    CreateTheme,
    UpdateTheme,
    SaveTheme,
    DeleteTheme,
    CloneTheme,
    GetConfig,
    SaveConfig,
    GetMatchesHistory,
    UploadMedia,
    UploadBatchMedia,
    ShowCover,
    ShowGame,
    StartGame,
    PauseGame,
    ResumeGame,
    FinishEarly,
    ResetGame,
    GetRealtimeState
};
